import {
  canonicalEventTypeForDisplay,
  getActivityEventTitle,
  getLocationPermissionResultTitle,
  getNotificationPermissionResultTitle,
} from './activityEventLabels';
import {
  getScreenViewUserDetailPhrase,
  isScreenViewLikeEvent,
} from './activityScreenLabels';

function mergeActivityData(activity) {
  const rawData = activity?.data && typeof activity.data === 'object' ? activity.data : {};
  const rawEventData =
    activity?.eventData && typeof activity.eventData === 'object' ? activity.eventData : {};
  return { ...rawEventData, ...rawData };
}

function capitalizeFirst(text) {
  if (!text) return text;
  return text.charAt(0).toLocaleUpperCase('tr-TR') + text.slice(1);
}

function formatDeviceLabel(raw) {
  if (!raw) return null;
  const key = String(raw).trim().toLowerCase();
  const map = {
    ios: 'iOS',
    android: 'Android',
    web: 'Web',
  };
  if (map[key]) return map[key];
  return capitalizeFirst(String(raw).trim());
}

function formatChannelLabel(raw) {
  if (!raw) return null;
  const key = String(raw).trim().toLowerCase().replace(/[_\s-]+/g, '');
  const map = {
    tckimlik: 'TC kimlik',
    tc: 'TC kimlik',
    password: 'Şifre',
    ios: 'iOS',
    android: 'Android',
    email: 'e-posta',
    phone: 'telefon',
    sms: 'SMS',
  };
  return map[key] || String(raw).trim();
}

function getCampaignDisplayLabel(data) {
  if (!data || typeof data !== 'object') return null;
  const title =
    data.campaignTitle ||
    data.campaignName ||
    data.title ||
    data.name ||
    data.data?.campaignTitle ||
    data.data?.campaignName ||
    data.data?.title;
  if (title !== undefined && title !== null && String(title).trim() !== '') {
    const t = String(title).trim();
    return t.length > 80 ? `${t.slice(0, 77)}…` : t;
  }
  const id = data.campaignId ?? data.data?.campaignId;
  if (id !== undefined && id !== null && String(id).trim() !== '') {
    return `Kampanya #${String(id).trim()}`;
  }
  return null;
}

function resolveLimitIncreaseOutcome(data) {
  const result = data?.limitIncreaseResult;
  if (!result || typeof result !== 'object') return null;
  if (typeof result.success === 'boolean') return result.success;

  const status = String(result.statusText || result.result || result.message || '')
    .trim()
    .toLocaleLowerCase('tr-TR');

  if (!status) return null;
  if (status.includes('onay') || status.includes('kabul') || status.includes('olumlu') || status.includes('başarılı')) {
    return true;
  }
  if (status.includes('red') || status.includes('olumsuz') || status.includes('başarısız')) {
    return false;
  }
  return null;
}

function resolveOnlinePaymentOutcome(data) {
  if (!data || typeof data !== 'object') return null;
  const success = data.success ?? data.paymentSuccess ?? data.isSuccess;
  if (typeof success === 'boolean') return success;
  const status = String(data.status || data.result || data.paymentStatus || '')
    .trim()
    .toLocaleLowerCase('tr-TR');
  if (!status) return null;
  if (status.includes('success') || status.includes('başarı')) return true;
  if (status.includes('fail') || status.includes('error') || status.includes('başarısız')) return false;
  return null;
}

function getRatingScore(data) {
  const req =
    data?.requestData ||
    data?.eventData?.requestData ||
    data?.EventData?.requestData ||
    null;
  const rawPoint =
    req?.point ??
    req?.Point ??
    data?.point ??
    data?.rating ??
    data?.score;
  if (rawPoint === undefined || rawPoint === null || rawPoint === '') return null;
  const n = Number(rawPoint);
  if (Number.isNaN(n) || n < 0) return null;
  return n % 1 === 0 ? String(n) : String(Math.round(n * 10) / 10);
}

function getSessionTitle(key, data, activity) {
  const device = formatDeviceLabel(
    data.device || data.deviceType || data.platform || activity?.platform,
  );
  if (device) {
    const phrase = key === 'session_end' ? 'oturumunu sonlandırdı' : 'oturumunu başlattı';
    return `${device} cihazda ${phrase}`;
  }
  return key === 'session_end' ? 'Oturumunu sonlandırdı' : 'Oturumunu başlattı';
}

function getMeaningfulExtraDetail(data) {
  const meaningful = [
    data.message,
    data.description,
    data.action,
  ].find((v) => typeof v === 'string' && v.trim() && /[a-zA-Z0-9çğıöşüÇĞİÖŞÜ]/.test(v));

  return meaningful ? String(meaningful).trim() : null;
}

/**
 * Kullanıcı detay — son aktiviteler ana satırı (birleşik, okunabilir cümle).
 */
export function getUserActivityDisplayTitle(activity) {
  const data = mergeActivityData(activity);
  const eventType = activity?.eventType;
  const key = canonicalEventTypeForDisplay(eventType);

  if (
    key === 'limit_increase_requested' ||
    key === 'limit_increase_confirmed' ||
    key === 'limit_increase_result'
  ) {
    const outcome = resolveLimitIncreaseOutcome(data);
    if (outcome === true) return 'Limit artırma talebi onaylandı';
    if (outcome === false) return 'Limit artırma talebi reddedildi';
    if (key === 'limit_increase_requested') return 'Limit artırma talebinde bulundu';
    return getActivityEventTitle(eventType);
  }

  if (key === 'sms_verification_result') {
    const status = String(data.status ?? '').trim().toLowerCase();
    if (status === 'success' || status === 'başarılı' || status === 'true') {
      return 'SMS doğrulamasını tamamladı';
    }
    if (status === 'fail' || status === 'failed' || status === 'error' || status === 'başarısız' || status === 'false') {
      return 'SMS doğrulaması başarısız oldu';
    }
    return 'SMS doğrulaması yaptı';
  }

  if (key === 'notification_permission_result') {
    return getNotificationPermissionResultTitle(data);
  }

  if (key === 'location_permission_result') {
    return getLocationPermissionResultTitle(data);
  }

  if (key === 'online_payment_result' || key === 'online_payment_3ds_result') {
    const outcome = resolveOnlinePaymentOutcome(data);
    if (outcome === true) {
      return key === 'online_payment_3ds_result'
        ? '3D Secure ödemeyi tamamladı'
        : 'Çevrimiçi ödemeyi tamamladı';
    }
    if (outcome === false) {
      return key === 'online_payment_3ds_result'
        ? '3D Secure ödeme başarısız oldu'
        : 'Çevrimiçi ödeme başarısız oldu';
    }
    return getActivityEventTitle(eventType);
  }

  if (isScreenViewLikeEvent(eventType, data)) {
    return getScreenViewUserDetailPhrase(data, eventType);
  }

  if (key === 'campaign_viewed' || key === 'campaign_clicked') {
    const camp = getCampaignDisplayLabel(data);
    if (camp) {
      return key === 'campaign_viewed'
        ? `${camp} kampanyasını görüntüledi`
        : `${camp} kampanyasına tıkladı`;
    }
  }

  if (key === 'customer_rating_submitted') {
    const score = getRatingScore(data);
    if (score) return `Uygulamaya ${score} puan verdi`;
    return 'Uygulamaya puan verdi';
  }

  if (key === 'session_start' || key === 'session_end') {
    return getSessionTitle(key, data, activity);
  }

  if (key === 'user_login') {
    const channel = formatChannelLabel(
      data.channel || data.loginMethod || data.provider || data.method,
    );
    if (channel) return `${channel} ile giriş yaptı`;
    return 'Giriş yaptı';
  }

  if (key === 'user_register') {
    const channel = formatChannelLabel(data.channel || data.source || data.provider);
    if (channel) return `${channel} üzerinden kayıt oldu`;
    return 'Kayıt oldu';
  }

  if (key === 'payment_method_selected') {
    const method = data.paymentMethod || data.method || data.selectedMethod || data.payment_method;
    if (method) return `${method} ödeme yöntemini seçti`;
    return 'Ödeme yöntemi seçti';
  }

  if (key === 'qr_code_generated') {
    const amount = data.amount || data.totalAmount || data.price;
    const code = data.code || data.qrCode || data.qr_id;
    if (amount && code) return `QR kod oluşturdu (${amount})`;
    if (amount) return `${amount} tutarında QR kod oluşturdu`;
    return 'QR kod oluşturdu';
  }

  return getActivityEventTitle(eventType);
}

/**
 * Ana satırda gösterilmeyen ek detay; yoksa null (alt satır render edilmez).
 */
export function getUserActivityDisplayDetail(activity) {
  const data = mergeActivityData(activity);
  const key = canonicalEventTypeForDisplay(activity?.eventType);

  if (isScreenViewLikeEvent(activity?.eventType, data)) {
    return null;
  }

  if (key === 'session_start' || key === 'session_end') {
    const reason = data.reason || data.closeReason;
    return reason ? `Neden: ${reason}` : null;
  }

  if (key === 'user_login') {
    const ip = data.ip || data.ipAddress;
    return ip ? `IP: ${ip}` : null;
  }

  if (key === 'qr_code_generated') {
    const code = data.code || data.qrCode || data.qr_id;
    const amount = data.amount || data.totalAmount || data.price;
    if (code && amount) return `Kod: ${code}`;
    if (code) return `Kod: ${code}`;
    return null;
  }

  if (
    key === 'limit_increase_requested' ||
    key === 'limit_increase_confirmed' ||
    key === 'customer_rating_submitted' ||
    key === 'upcoming_installments_view' ||
    key === 'upcoming_installments_detail_click' ||
    key === 'transactions_view_all_click' ||
    key === 'password_set_success' ||
    key === 'password_reset_success' ||
    key === 'user_register' ||
    key === 'notification_permission_result' ||
    key === 'location_permission_result'
  ) {
    return null;
  }

  return getMeaningfulExtraDetail(data);
}
